import { type ReactNode } from 'react';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800',
  primary: 'bg-blue-100 text-blue-800',
  success: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-cyan-100 text-cyan-800',
  secondary: 'bg-purple-100 text-purple-800',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

const dotStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-500',
  primary: 'bg-blue-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-cyan-500',
  secondary: 'bg-purple-500',
};

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  className = '',
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotStyles[variant]} mr-1.5`} />}
      {children}
    </span>
  );
}

export function getStatusBadge(status: string): ReactNode {
  const statusConfig: Record<string, { variant: BadgeVariant; label: string }> = {
    pending: { variant: 'warning', label: 'En attente' },
    confirmed: { variant: 'info', label: 'Confirmee' },
    in_progress: { variant: 'primary', label: 'En cours' },
    delivered: { variant: 'success', label: 'Livree' },
    confirmed_livree: { variant: 'success', label: 'Confirmee livree' },
    completed: { variant: 'success', label: 'Terminee' },
    cancelled: { variant: 'danger', label: 'Annulee' },
    rejected: { variant: 'danger', label: 'Rejetee' },
    active: { variant: 'success', label: 'Actif' },
    inactive: { variant: 'default', label: 'Inactif' },
    suspended: { variant: 'danger', label: 'Suspendu' },
    valid: { variant: 'success', label: 'Validé' },
    dismiss: { variant: 'danger', label: 'Rejeté' },
    verified: { variant: 'success', label: 'Verifie' },
    unverified: { variant: 'warning', label: 'Non verifie' },
    draft: { variant: 'default', label: 'Brouillon' },
    published: { variant: 'success', label: 'Publiee' },
  };

  const config = statusConfig[status] || { variant: 'default' as BadgeVariant, label: status };

  return <Badge variant={config.variant} dot>{config.label}</Badge>;
}
